import React, { useState, useEffect } from 'react';
import { FolderOpen } from 'lucide-react';
import type { ResultSectionConfig, ResultsColumnConfig, SearchResponse, ItemsPerRow, Product, ColumnCount } from '../utils/types';
import { DEFAULT_RESULTS_LAYOUT, DEFAULT_RESULTS_HEADER_SECTIONS, DEFAULT_RESULTS_COLUMNS } from '../utils/types';
import AIContextBanner from './AIContextBanner';
import { CollectionTiles } from './CollectionTiles';
import { useI18n } from '../i18n/I18nContext';
import { useFontSizes } from '../contexts/FontSizeContext';
import { ProductResultsWidget } from './widgets/ProductResultsWidget';
import { ArticleResultsWidget } from './widgets/ArticleResultsWidget';
import { PageResultsWidget } from './widgets/PageResultsWidget';
import { InlineBannerWidget } from './widgets/InlineBannerWidget';

// Grid classes for results columns (same as LayoutRenderer)
const RESULTS_COL_GRID: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3',
};

interface ResultsRendererProps {
  /** @deprecated Use resultsHeaderSections + resultsColumns instead */
  resultsLayout?: ResultSectionConfig[];
  resultsColumnCount?: ColumnCount;
  resultsHeaderSections?: ResultSectionConfig[];
  resultsColumns?: ResultsColumnConfig[];
  autoCollapseEmptyColumns?: boolean;
  results: SearchResponse;
  itemsPerRow: ItemsPerRow;
  hasMore: boolean;
  isLoadingMore: boolean;
  isAIThinking: boolean;
  onProductClick: (product: Product) => void;
  onLoadMore: () => void;
}

export const ResultsRenderer: React.FC<ResultsRendererProps> = ({
  resultsLayout,
  resultsColumnCount,
  resultsHeaderSections,
  resultsColumns,
  autoCollapseEmptyColumns,
  results,
  itemsPerRow,
  hasMore,
  isLoadingMore,
  isAIThinking,
  onProductClick,
  onLoadMore,
}) => {
  const { t } = useI18n();
  const { heading } = useFontSizes();

  // JS breakpoint for column stacking (conditional render, not CSS hide — host
  // CSS can override `.botilia-show-sm-*` in the embed).
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 640,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const handler = (e: MediaQueryListEvent | MediaQueryList) => setIsMobile(e.matches);
    handler(mq);
    mq.addEventListener('change', handler as (e: MediaQueryListEvent) => void);
    return () => mq.removeEventListener('change', handler as (e: MediaQueryListEvent) => void);
  }, []);

  // Collections whose name matches the query — shown as tiles above the results
  const collectionsStrip = (results.collections?.length ?? 0) > 0 ? (
    <section className="mb-4 sm:mb-6 animate-fade-in">
      <h2 className={`flex items-center gap-2 ${heading} font-semibold uppercase tracking-wider text-[#020202] mb-2 sm:mb-3`}>
        <FolderOpen className="h-4 w-4" />
        {t('section.collections')}
      </h2>
      <CollectionTiles collections={results.collections!} itemsPerRow={isMobile ? 2 : 4} />
    </section>
  ) : null;

  // Render a single section by type
  function renderSection(section: ResultSectionConfig): React.ReactNode {
    if (!section.visible) return null;
    switch (section.type) {
      case 'ai-context':
        return results.aiContext ? (
          <AIContextBanner
            key={section.id}
            context={results.aiContext}
            isThinking={isAIThinking}
          />
        ) : null;

      case 'product-results':
        return (
          <ProductResultsWidget
            key={section.id}
            config={section}
            products={results.products}
            total={results.total}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
            itemsPerRow={section.itemsPerRow ?? itemsPerRow}
            onProductClick={onProductClick}
            onLoadMore={onLoadMore}
          />
        );

      case 'article-results':
        return (
          <ArticleResultsWidget
            key={section.id}
            config={section}
            articles={results.articles}
          />
        );

      case 'page-results':
        return (
          <PageResultsWidget
            key={section.id}
            config={section}
            pages={results.pages}
          />
        );

      case 'banner-inline':
        return (
          <InlineBannerWidget
            key={section.id}
            config={section}
          />
        );

      default:
        return null;
    }
  }

  // Check whether a column has any content to render given current results
  function columnHasContent(col: ResultsColumnConfig): boolean {
    return col.sections.some((s) => {
      if (!s.visible) return false;
      switch (s.type) {
        case 'product-results': return results.products.length > 0;
        case 'article-results': return (results.articles?.length ?? 0) > 0;
        case 'page-results': return (results.pages?.length ?? 0) > 0;
        case 'ai-context': return !!results.aiContext;
        case 'banner-inline': return true; // banners fetch independently
        default: return false;
      }
    });
  }

  // New column-based rendering path
  if (resultsColumns) {
    const headerSections = resultsHeaderSections ?? DEFAULT_RESULTS_HEADER_SECTIONS;
    const colCount = resultsColumnCount ?? 1;
    const allColumns = resultsColumns.slice(0, colCount);

    // Auto-collapse: filter out empty columns (default true)
    const shouldCollapse = autoCollapseEmptyColumns !== false;
    const columns = shouldCollapse && colCount > 1
      ? allColumns.filter((col) => columnHasContent(col))
      : allColumns;
    const effectiveColCount = columns.length;
    const hasCustomWidths = effectiveColCount > 1 && columns.some((c) => c.widthPercent != null);

    const renderColumns = () =>
      columns.map((col) => {
        const w = effectiveColCount === 1
          ? 100
          : (col.widthPercent ?? Math.floor(100 / effectiveColCount));
        const gapPx = 16; // gap-4 = 16px
        return (
          <div
            key={col.id}
            className="min-w-0"
            style={hasCustomWidths && effectiveColCount > 1 && !isMobile ? { width: `calc(${w}% - ${((effectiveColCount - 1) * gapPx) / effectiveColCount}px)` } : undefined}
          >
            {col.sections.filter((s) => s.visible).map((section) => renderSection(section))}
          </div>
        );
      });

    return (
      <>
        {/* Full-width header sections (e.g., AI Context) */}
        {headerSections.filter((s) => s.visible).map((section) => renderSection(section))}

        {/* Matching collections */}
        {collectionsStrip}

        {/* Column layout */}
        {columns.length > 0 && (
          (hasCustomWidths && effectiveColCount > 1) ? (
            // Custom widths: side-by-side on desktop, stacked on mobile
            <div className={isMobile ? 'flex flex-col gap-4' : 'flex gap-4'}>
              {renderColumns()}
            </div>
          ) : (
            <div className={`grid ${RESULTS_COL_GRID[effectiveColCount]} gap-4`}>
              {renderColumns()}
            </div>
          )
        )}
      </>
    );
  }

  // Legacy flat rendering path (backward compat)
  const sections = resultsLayout ?? DEFAULT_RESULTS_LAYOUT;

  return (
    <>
      {collectionsStrip}
      {sections.filter((s) => s.visible).map((section) => renderSection(section))}
    </>
  );
};
